import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Botao, Campo, Cartao, EstadoCarregando } from '../componentes/ui';
import { requisicaoPublica } from '../servicos/http';
import { ErroDaApi } from './ErroDaApi';

/**
 * "Crie sua senha" — o destino do link que o cadastro de colaborador manda.
 *
 * ## Abrir não gasta o link
 *
 * A tela pergunta primeiro se o link ainda vale (`GET /auth/definir-senha/:token`)
 * e de quem é, para cumprimentar a pessoa e mostrar o login dela. Só o envio da
 * senha consome o link: filtro de e-mail corporativo costuma abrir links para
 * checar se são seguros, e se a abertura contasse, a pessoa chegaria num link
 * já usado.
 *
 * ## Depois, entrar
 *
 * Sem sessão automática, como na recuperação: criar a senha e entrar são duas
 * coisas, e a tela de entrada já vem com o e-mail preenchido.
 */
export function CriarSenha(): JSX.Element {
  const { token = '' } = useParams();
  const navegar = useNavigate();
  const [senha, setSenha] = useState('');
  const [confirmacao, setConfirmacao] = useState('');
  const [mostrar, setMostrar] = useState(false);

  const link = useQuery({
    queryKey: ['link-de-senha', token],
    queryFn: () =>
      requisicaoPublica<{ nome: string; email: string }>(
        `/auth/definir-senha/${encodeURIComponent(token)}`,
      ),
    retry: false,
    staleTime: Infinity,
  });

  const criar = useMutation({
    mutationFn: () =>
      requisicaoPublica<unknown>('/auth/definir-senha', {
        metodo: 'POST',
        corpo: { token, novaSenha: senha },
      }),
  });

  const senhasBatem = senha !== '' && senha === confirmacao;

  return (
    <main className="mx-auto flex min-h-dvh w-full max-w-md flex-col justify-center gap-6 px-4 py-12">
      <header className="text-center">
        <p className="font-display text-2xl font-semibold tracking-tight">Deca</p>
        <p className="mt-1 text-sm text-conteudo-secundario">MS3 Capital · Créditos de ICMS</p>
      </header>

      {link.isPending ? (
        <EstadoCarregando contexto="o seu link" />
      ) : link.isError ? (
        <Cartao como="section" titulo="Este link não vale mais">
          <div className="flex flex-col gap-4">
            <p className="text-sm text-conteudo-secundario">
              O link de criação de senha vale por 7 dias e só uma vez. Peça um novo a quem fez o seu
              cadastro — ou, se você já tem acesso, recupere a senha pelo e-mail.
            </p>
            <Botao variante="secundario" larguraTotal onClick={() => navegar('/recuperar-senha')}>
              Esqueci minha senha
            </Botao>
          </div>
        </Cartao>
      ) : criar.isSuccess ? (
        <Cartao como="section" titulo="Senha criada">
          <div className="flex flex-col gap-4">
            <p className="text-sm text-conteudo-secundario">
              Pronto, {link.data.nome.split(' ')[0]}. Entre com o e-mail{' '}
              <strong className="text-conteudo-primario">{link.data.email}</strong> e a senha que
              você acabou de criar.
            </p>
            <Botao
              larguraTotal
              onClick={() =>
                navegar('/entrar', { replace: true, state: { email: link.data.email } })
              }
            >
              Entrar no Deca
            </Botao>
          </div>
        </Cartao>
      ) : (
        <Cartao
          como="section"
          titulo="Crie sua senha"
          subtitulo={`Olá, ${link.data.nome}. Seu login é ${link.data.email}.`}
        >
          <form
            className="flex flex-col gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (senhasBatem) criar.mutate();
            }}
          >
            <Campo
              rotulo="Nova senha"
              type={mostrar ? 'text' : 'password'}
              required
              autoComplete="new-password"
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
              sufixo={
                <button
                  type="button"
                  className="text-xs text-conteudo-secundario underline-offset-2 hover:underline"
                  aria-pressed={mostrar}
                  onClick={() => setMostrar((v) => !v)}
                >
                  {mostrar ? 'Ocultar' : 'Mostrar'}
                </button>
              }
            />
            <Campo
              rotulo="Repita a senha"
              type={mostrar ? 'text' : 'password'}
              required
              autoComplete="new-password"
              value={confirmacao}
              onChange={(e) => setConfirmacao(e.target.value)}
            />

            {confirmacao !== '' && !senhasBatem && (
              <p className="text-xs text-erro">As senhas não são iguais.</p>
            )}

            {criar.isError && (
              <ErroDaApi titulo="Não foi possível criar a senha" erro={criar.error} />
            )}

            <Botao type="submit" carregando={criar.isPending} disabled={!senhasBatem} larguraTotal>
              Criar senha
            </Botao>
          </form>
        </Cartao>
      )}

      <p className="text-center text-sm text-conteudo-secundario">
        Já tem senha?{' '}
        <Link to="/entrar" className="text-acento-ciano underline-offset-4 hover:underline">
          Ir para o acesso
        </Link>
      </p>
    </main>
  );
}
